var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { GenericError, TimeoutError } from '@auth0/auth0-spa-js';
import { CLEANUP_IFRAME_TIMEOUT_IN_SECONDS, DEFAULT_AUTHORIZE_TIMEOUT_IN_SECONDS } from './constants';
export const parseQueryResult = (queryString) => {
    if (queryString.indexOf('#') > -1) {
        queryString = queryString.substr(0, queryString.indexOf('#'));
    }
    const queryParams = queryString.split('&');
    const parsedQuery = {};
    queryParams.forEach(qp => {
        const [key, val] = qp.split('=');
        parsedQuery[key] = decodeURIComponent(val);
    });
    if (parsedQuery.expires_in) {
        parsedQuery.expires_in = parseInt(parsedQuery.expires_in);
    }
    return parsedQuery;
};
export const runIframe = (authorizeUrl, eventOrigin, timeoutInSeconds = DEFAULT_AUTHORIZE_TIMEOUT_IN_SECONDS) => {
    return new Promise((res, rej) => {
        const iframe = window.document.createElement('iframe');
        iframe.setAttribute('width', '0');
        iframe.setAttribute('height', '0');
        iframe.style.display = 'none';
        const removeIframe = () => {
            if (window.document.body.contains(iframe)) {
                window.document.body.removeChild(iframe);
                window.removeEventListener('message', iframeEventHandler, false);
            }
        };
        const timeoutSetTimeoutId = setTimeout(() => {
            rej(new TimeoutError());
            removeIframe();
        }, timeoutInSeconds * 1000);
        const iframeEventHandler = function (e) {
            if (e.origin != eventOrigin)
                return;
            if (!e.data || e.data.type !== 'authorization_response')
                return;
            const eventSource = e.source;
            if (eventSource) {
                eventSource.close();
            }
            e.data.response.error
                ? rej(GenericError.fromPayload(e.data.response))
                : res(e.data.response);
            clearTimeout(timeoutSetTimeoutId);
            window.removeEventListener('message', iframeEventHandler, false);
            // Delay the removal of the iframe to prevent hanging loading status
            // in Chrome: https://github.com/auth0/auth0-spa-js/issues/240
            setTimeout(removeIframe, CLEANUP_IFRAME_TIMEOUT_IN_SECONDS * 1000);
        };
        window.addEventListener('message', iframeEventHandler, false);
        window.document.body.appendChild(iframe);
        iframe.setAttribute('src', authorizeUrl);
    });
};
export const getCrypto = () => {
    //ie 11.x uses msCrypto
    return (window.crypto || window.msCrypto);
};
export const getCryptoSubtle = () => {
    const crypto = getCrypto();
    //safari 10.x uses webkitSubtle
    return crypto.subtle || crypto.webkitSubtle;
};
export const createRandomString = () => {
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.';
    let random = '';
    const randomValues = Array.from(getCrypto().getRandomValues(new Uint8Array(43)));
    randomValues.forEach(v => (random += charset[v % charset.length]));
    return random;
};
export const encode = (value) => btoa(value);
export const decode = (value) => atob(value);
export const createQueryParams = (params) => {
    return Object.keys(params)
        .filter(k => typeof params[k] !== 'undefined')
        .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
        .join('&');
};
export const sha256 = (s) => __awaiter(void 0, void 0, void 0, function* () {
    const digestOp = getCryptoSubtle().digest({ name: 'SHA-256' }, new TextEncoder().encode(s));
    // msCrypto (IE11) uses the old spec, which is not Promise based
    // https://msdn.microsoft.com/en-us/expression/dn904640(v=vs.71)
    // Instead of returning a promise, it returns a CryptoOperation
    // with a result property in it.
    // As a result, the various events need to be handled in the event that we're
    // working in IE11 (hence the msCrypto check). These events just call resolve
    // or reject depending on their intention.
    if (window.msCrypto) {
        return new Promise((res, rej) => {
            digestOp.oncomplete = (e) => {
                res(e.target.result);
            };
            digestOp.onerror = (e) => {
                rej(e.error);
            };
            digestOp.onabort = () => {
                rej('The digest operation was aborted');
            };
        });
    }
    return yield digestOp;
});
const urlEncodeB64 = (input) => {
    const b64Chars = { '+': '-', '/': '_', '=': '' };
    return input.replace(/[+/=]/g, (m) => b64Chars[m]);
};
export const bufferToBase64UrlEncoded = (input) => {
    const ie11SafeInput = new Uint8Array(input);
    return urlEncodeB64(window.btoa(String.fromCharCode(...Array.from(ie11SafeInput))));
};
export const validateCrypto = () => {
    if (!getCrypto()) {
        throw new Error('For security reasons, `window.crypto` is required to run `auth0-spa-js`.');
    }
    if (typeof getCryptoSubtle() === 'undefined') {
        throw new Error(`
      auth0-spa-js must run on a secure origin. See https://github.com/auth0/auth0-spa-js/blob/master/FAQ.md#why-do-i-get-auth0-spa-js-must-run-on-a-secure-origin for more information.
    `);
    }
};
//# sourceMappingURL=utils.js.map